import React, { useEffect, useMemo, useRef, useState } from "react";
import { Cell, Funnel, FunnelChart, LabelList } from "recharts";

import { ChartDataPoint } from "@/types/chart";
import { useChartDimensionsV2 } from "@/hooks/useChartDimensions";
import { useCurrentWorkspace } from "@/store/authContext/authContext";
import { formatNumberLong as formatNumber } from "@/common/valueFormatter";
import { darkenColor } from "@/common/colorUtils";
import type { HandleLegendItemChangeArguments } from "@/types/report";
import { SeriesConfig } from "@/common/type/report-types";
import { ReportChartSettingsV2 } from "@/common/report-configuration";
import { ReportExtras } from "@/common/report-configuration";

/**
 * Interpolates between two hex colors
 * @param color1 Starting hex color (e.g., "#B3D4E5")
 * @param color2 Ending hex color (e.g., "#5FB35F")
 * @param factor Number between 0 and 1 (0 = color1, 1 = color2)
 */
const interpolateColor = (
  color1: string,
  color2: string,
  factor: number,
): string => {
  // Parse hex colors
  const c1 = parseInt(color1.slice(1), 16);
  const c2 = parseInt(color2.slice(1), 16);

  const r1 = (c1 >> 16) & 0xff;
  const g1 = (c1 >> 8) & 0xff;
  const b1 = c1 & 0xff;

  const r2 = (c2 >> 16) & 0xff;
  const g2 = (c2 >> 8) & 0xff;
  const b2 = c2 & 0xff;

  // Interpolate
  const r = Math.round(r1 + factor * (r2 - r1));
  const g = Math.round(g1 + factor * (g2 - g1));
  const b = Math.round(b1 + factor * (b2 - b1));

  // Convert back to hex
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

/**
 * Generates an array of colors interpolated between start and end colors
 * @param count Number of colors to generate
 * @param startColor Starting hex color (light blue)
 * @param endColor Ending hex color (parita blue)
 */
const generateColorGradient = (
  count: number,
  startColor: string = "#CCDEFF",
  endColor: string = "#3274EA",
): string[] => {
  if (count <= 1) return [startColor];
  return Array.from({ length: count }, (_, i) => {
    const factor = i / (count - 1);
    if (factor === 1) {
      return "#5EB6AB";
    }
    return interpolateColor(startColor, endColor, factor);
  });
};

/**
 * Custom label component to render percentage badges between funnel slices
 * This component receives props from recharts with positioning information
 */
interface PercentageLabelProps {
  x?: number | string;
  y?: number | string;
  width?: number | string;
  height?: number | string;
  value?: number | string | boolean | null;
  index?: number;
  payload?: { value: number; name: string; percentage: string | null };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

const PercentageLabel = (
  props: PercentageLabelProps,
): React.JSX.Element | null => {
  const { x = 0, y = 0, width = 0, height = 0, value } = props;

  const filterId = useMemo(
    () => `percentage-label-shadow-${Math.random().toString(36).slice(2)}`,
    [],
  );

  const [percentageValue, percentageFill] = (value?.toString() || "").split(
    "-",
  );
  // Don't render label for the last item

  if (
    percentageValue === "null" ||
    percentageValue === undefined ||
    percentageValue === ""
  ) {
    return null;
  }

  // Position badge between slices (at the bottom edge of current slice)
  const badgeY = Number(y) + Number(height);
  const badgeX = Number(x);

  return (
    <>
      {/* Drop shadow for better contrast against the funnel */}
      <defs>
        <filter id={filterId} x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow
            dx="0"
            dy="-2"
            stdDeviation="3"
            floodColor="#000000"
            floodOpacity="0.25"
          />
        </filter>
      </defs>
      <g
        transform={`translate(${Number(width) / 2}, 0)`}
        filter={`url(#${filterId})`}
      >
        {/* Badge background */}
        <rect
          x={badgeX - 25}
          y={badgeY - 11}
          width={50}
          height={22}
          fill={percentageFill}
          stroke="none"
        />
        {/* Percentage text */}
        <text
          x={badgeX}
          y={badgeY + 1}
          textAnchor="middle"
          fill="#ffffff"
          fontSize="13"
          fontWeight="700"
          dominantBaseline="middle"
        >
          {percentageValue}
        </text>
        {/* Triangle pointer */}
        <path
          d={`
          M ${badgeX - 6} ${badgeY + 10}
          L ${badgeX} ${badgeY + 16}
          L ${badgeX + 6} ${badgeY + 10}
          Z
        `}
          fill={percentageFill}
          stroke="none"
        />
      </g>
    </>
  );
};

/**
 * Vertical arrow component with metric display
 */
interface VerticalArrowProps {
  mainValue: string;
  label: string;
  height?: number;
  color?: string;
}

const VerticalArrow: React.FC<VerticalArrowProps> = ({
  mainValue,
  label,
  height = 400,
  color = "#CCCCCC",
}) => {
  const arrowWidth = 40;
  const lineWidth = 4;
  const arrowHeadHeight = 18;
  const arrowHeadWidth = 14;
  const adjustedHeight = 50;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "start",
        alignItems: "start",
        position: "relative",
        marginTop: "30px",
      }}
    >
      {/* Arrow line and head */}
      <svg width={arrowWidth} height={height - adjustedHeight}>
        {/* Vertical line */}
        <rect
          x={(arrowWidth - lineWidth) / 2}
          y={0}
          width={lineWidth}
          height={height - adjustedHeight - arrowHeadHeight}
          fill={color}
        />

        {/* Arrow head */}
        <path
          d={`
            M ${arrowWidth / 2} ${height - adjustedHeight}
            L ${arrowWidth / 2 - arrowHeadWidth / 2} ${height - adjustedHeight - arrowHeadHeight}
            L ${arrowWidth / 2 + arrowHeadWidth / 2} ${height - adjustedHeight - arrowHeadHeight}
            Z
          `}
          fill={color}
        />
      </svg>
      <div
        style={{ position: "absolute", top: "50%", left: `${arrowWidth}px` }}
      >
        {/* Main value */}
        <div
          style={{
            fontSize: "40px",
            fontWeight: "bold",
            color: "#333",
            lineHeight: "1",
          }}
        >
          {mainValue}
        </div>

        {/* Label */}
        <div
          style={{
            fontSize: "11px",
            color: "#666",
            lineHeight: "1",
          }}
        >
          {label}
        </div>
      </div>
    </div>
  );
};

interface FunnelChartComponentV2Props {
  data: ChartDataPoint[];
  reportExtras: ReportExtras;
  xAxis: string;
  xAxisLabel: string;
  yAxisLeftLabel: string;
  seriesConfig: SeriesConfig;
  chartSettings: ReportChartSettingsV2;
  height?: number;
  maxHeight?: number;
  handleLegendItemChange: (args: HandleLegendItemChangeArguments) => void;
  stacked?: boolean;
}

const FunnelChartComponentV2: React.FC<FunnelChartComponentV2Props> = ({
  data: _data,
  reportExtras,
  chartSettings,
  maxHeight,
  seriesConfig,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(400);

  // Measure container width on mount and resize
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        if (width > 0) {
          setContainerWidth(width);
        }
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const dimensions = useChartDimensionsV2(
      chartSettings,
      maxHeight,
      containerWidth,
    ),
    currentWorkspace = useCurrentWorkspace();

  // Transform ChartDataPoint[] into funnel format
  // Expected input: array of objects with name (stage name), measure value (numeric), axis_group
  // Output: array filtered (no zero values) with { value, name, fill } structure
  // Data comes pre-ordered, so no sorting is applied
  const funnelData = useMemo(() => {
    if (!_data || _data.length === 0) {
      return [];
    }

    // Get the measure key from seriesConfig (e.g., "Headcount", "Turnover Rate")
    const measureKey = Array.from(seriesConfig.values())[0]?.key || "MEASURE";

    const MAX_WIDTH = 300;
    const MIN_WIDTH = 150;
    // Filter out items with measure=0 or empty measure
    const filteredData = _data.filter((item) => {
      const measureValue = item[measureKey];
      return (
        measureValue !== undefined && measureValue !== "" && measureValue !== 0
      );
    });
    const recruitmentStageNames =
      currentWorkspace.recruitmentStageColumnNames || [];
    // Sort by recruitment stage order
    filteredData.sort((a, b) => {
      const indexA = recruitmentStageNames.indexOf(a.name as string);
      const indexB = recruitmentStageNames.indexOf(b.name as string);
      return indexA - indexB;
    });

    // Generate color gradient based on filtered data length
    const colors = generateColorGradient(filteredData.length);

    // Map to funnel format
    const series = filteredData.map((item, index) => {
      const stageName = item.name as string;
      // Funnels look weird with actual data so the width is based on a synthetic scale
      // from MAX_WIDTH to MIN_WIDTH over the number of elements
      const width =
        MAX_WIDTH -
        ((MAX_WIDTH - MIN_WIDTH) / (filteredData.length - 1)) * index;

      // Calculate percentage to next stage (if exists)
      const currentValue = item[measureKey] as number;
      const nextItem = filteredData[index + 1];
      let percentage: number | null = null;
      if (nextItem && nextItem[measureKey] !== undefined) {
        percentage = Math.round(
          ((nextItem[measureKey] as number) / currentValue) * 100,
        );
        if (percentage === Infinity || isNaN(percentage)) {
          percentage = null;
        }
      }
      const percentageString = percentage !== null ? `${percentage}%` : null;

      const labelStageName = `${stageName}`;
      const labelValue = `👤 ${formatNumber(currentValue)}`;

      return {
        value: currentValue,
        name: stageName,
        labelStageName,
        labelValue,
        fill: colors[index],
        width,
        percentage: `${percentageString}-${darkenColor(colors[index], 20)}`,
      };
    });
    const width =
      MAX_WIDTH - ((MAX_WIDTH - MIN_WIDTH) / (_data.length - 1)) * _data.length;
    series.push({
      value: 0,
      name: "",
      labelStageName: "",
      labelValue: "",
      fill: "#FFF",
      width,
      percentage: "",
    });
    return series;
  }, [_data, currentWorkspace.recruitmentStageColumnNames, seriesConfig]);
  console.log("Funnel Data:", funnelData);

  const MIN_CELL_HEIGHT = 66; // Min-height per funnel cell

  const calculatedHeight = useMemo(() => {
    const minHeightNeeded =
      funnelData.length * MIN_CELL_HEIGHT - MIN_CELL_HEIGHT; // Last item is zero-height
    const defaultHeight = dimensions.chartHeight;

    return Math.max(minHeightNeeded, defaultHeight);
  }, [funnelData.length, dimensions.chartHeight]);

  return (
    <div
      ref={containerRef}
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "start",
        justifyContent: "center",
        width: "100%",
        gap: "0px",
        transform: "translateX(100px)",
      }}
    >
      <div
        style={{
          maxWidth: "550px",
          width: "100%",
        }}
      >
        <h2 className="w-full text-center text-lg font-bold">
          {reportExtras.jobReqCount} Job Requisitions
        </h2>
        <FunnelChart
          width={dimensions.chartWidth}
          // height={dimensions.chartHeight * 1.5}
          height={calculatedHeight}
          responsive
          margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
          style={{ width: "100%", maxWidth: "100%" }}
        >
          {/* <ChartTooltipV2
            content={(props) => (
              <ChartTooltipV2Wrapper
                chartType="Funnel"
                seriesConfig={seriesConfig}
                {...props}
              />
            )}
          /> */}
          <Funnel dataKey="width" data={funnelData} isAnimationActive={false}>
            {funnelData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.fill}
                stroke="#fff"
                strokeWidth={3}
              />
            ))}

            <LabelList
              position="left"
              fill="#000"
              stroke="none"
              style={{
                transform: "translateX(0%)",
                fontSize: "14px",
                fontWeight: 500,
              }}
              dataKey="labelStageName"
            />

            <LabelList
              className="tester"
              position="center"
              fill="#000"
              stroke="none"
              style={{
                fontSize: "21px",
                fontWeight: 500,
              }}
              dataKey="labelValue"
            />
            <LabelList dataKey="percentage" content={PercentageLabel} />
          </Funnel>
        </FunnelChart>
      </div>
      <div
        style={{
          width: "150px",
        }}
      >
        {reportExtras.avgTimeToFill != 0 && (
          <VerticalArrow
            mainValue={`${reportExtras.avgTimeToFill}d`}
            label="Avg. Time to Fill"
            height={calculatedHeight}
            color="#CCCCCC"
          />
        )}
      </div>
    </div>
  );
};

export default React.memo(
  FunnelChartComponentV2,
) as typeof FunnelChartComponentV2;
